import React, { useEffect, useState } from 'react';
import { Modal } from 'react-responsive-modal';
import { Typography, Box, Stack, Grid, Switch, FormLabel } from '@mui/material';
import { MasterMessage } from '../../../../masters/masterMessages';
import ButtonGeneral from '../../../components/general/buttons/buttonGeneral';
import Save from '@mui/icons-material/Save';
import CancelIcon from '@mui/icons-material/Cancel';
import {
    EmojiEmotions,
    Favorite,
    FavoriteBorder,
    InsertEmoticon,
    RadioButtonChecked,
    RadioButtonUnchecked,
    Star,
    StarBorder,
    ThumbUpAlt,
    ThumbUpOffAlt,
} from '@mui/icons-material';
import FieldText from '../../../components/general/fields/fieldText';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import FieldSelect from '../../../components/general/fields/fieldSelect';
import {
    asyncPatchQuestion,
    asyncPostQuestion,
} from '../../../../services/survey.service';
import { SwalCustomClass } from '../../../components/general/toastSwal/swalCustomClass';
import Loading from '../../../components/general/loading/loading';
import { DependencyForm } from './dependencyForm';

export const typesSupported: any = {
    multiple: true,
    only: true,
    linear: true,
    'linear-label': true,
};

export const typesIcon = [
    {
        id: 'circle',
        name: 'Circulo',
        icon: (
            <RadioButtonUnchecked
                fontSize="large"
                style={{ fill: '#3366CC' }}
            />
        ),
        icon2: (
            <RadioButtonChecked fontSize="large" style={{ fill: '#3366CC' }} />
        ),
    },
    {
        id: 'star',
        name: 'Estrella',
        icon: <StarBorder fontSize="large" style={{ fill: '#3366CC' }} />,
        icon2: <Star fontSize="large" style={{ fill: '#3366CC' }} />,
    },
    {
        id: 'smile',
        name: 'Sonrisa',
        icon: <InsertEmoticon fontSize="large" style={{ fill: '#3366CC' }} />,
        icon2: <EmojiEmotions fontSize="large" style={{ fill: '#3366CC' }} />,
    },
    {
        id: 'like',
        name: 'Like',
        icon: <ThumbUpOffAlt fontSize="large" style={{ fill: '#3366CC' }} />,
        icon2: <ThumbUpAlt fontSize="large" style={{ fill: '#3366CC' }} />,
    },
    {
        id: 'heart',
        name: 'Corazón',
        icon: <FavoriteBorder fontSize="large" style={{ fill: '#3366CC' }} />,
        icon2: <Favorite fontSize="large" style={{ fill: '#3366CC' }} />,
    },
];

type Props = {
    open: boolean;
    onClose: (value: boolean) => void;
    saveLoad: (value: boolean) => void;
    idSurvey?: string;
    idQuestion?: string;
    question?: any | undefined;
    loading: false | boolean;
    questionsDependency: any[];
};

const ModalQuestion = ({
    open,
    onClose,
    saveLoad,
    idSurvey,
    idQuestion,
    question,
    loading,
    questionsDependency,
}: Props) => {
    const { questions }: any = MasterMessage.surveys;
    const [dependencies, setDependencies] = useState<any[]>([]);
    const [questionDependency, setQuestionDependency] = useState<any>();

    const getStartRange = () =>
        Number(questionDependency?.answers[0]?.range?.split('-')[0] || '1');

    const getEndRange = () =>
        Number(questionDependency?.answers[0]?.range?.split('-')[1] || '1');

    const formik = useFormik({
        initialValues: {
            surveyId: idSurvey,
            type: '',
            description: '',
            icon: 'circle',
            why: false,
            status: 'active',
            dependency: false,
            questionDependency: '',
            answerDependency: '',
            range1: '',
            range2: '',
        },
        validationSchema: Yup.object({
            type: Yup.string().required('El tipo de pregunta es obligatorio'),
            description: Yup.string().required('La descripción es obligatoria'),
            questionDependency: Yup.string()
                .nullable()
                .when(['dependency'], {
                    is: (dependency: any) => dependency,
                    then: (schema) =>
                        schema.required(
                            'La pregunta a depender es obligatoria'
                        ),
                }),
            answerDependency: Yup.string()
                .nullable()
                .when({
                    is: () => Boolean(questionDependency?.type === 'only'),
                    then: (schema) =>
                        schema.required(
                            'La respuesta a depender es obligatoria'
                        ),
                }),
            range1: Yup.number().when({
                is: () => Boolean(questionDependency?.type === 'linear'),
                then: (schema) =>
                    schema
                        .required('El rango a depender es obligatorio')
                        .typeError('El rango debe ser un número')
                        .min(
                            getStartRange(),
                            `El número debe ser mayor o igual a ${getStartRange()}`
                        )
                        .max(
                            Yup.ref('range2'),
                            `El número debe ser menor o igual al final del rango`
                        ),
            }),
            range2: Yup.number().when({
                is: () => Boolean(questionDependency?.type === 'linear'),
                then: (schema) =>
                    schema
                        .required('El rango a depender es obligatorio')
                        .typeError('El rango debe ser un número')
                        .min(
                            Yup.ref('range1'),
                            `El número debe ser mayor o igual al inicio del rango`
                        )
                        .max(
                            getEndRange(),
                            `El número debe ser menor o igual a ${getEndRange()}`
                        ),
            }),
        }),
        onSubmit: async (formModalQuestions) => {
            if (questionDependency?.type === 'linear') {
                formModalQuestions.answerDependency =
                    `${formModalQuestions.range1}-${formModalQuestions.range2}` ||
                    '';
            }

            if (!idQuestion) {
                if (
                    !['text', 'big-text'].find(
                        (type) => type === formModalQuestions.type
                    )
                ) {
                    formModalQuestions.status = 'in process';
                }
                const body: any = await asyncPostQuestion(
                    formModalQuestions
                ).finally(() => {
                    onClose(false);
                    saveLoad(true);
                    formik.handleReset;
                });
                if (body?.items !== undefined) {
                    SwalCustomClass.fire({
                        text: questions.messageNew,
                        icon: 'success',
                        iconColor: '#1d1d1d',
                    });
                } else if (body?.errors !== undefined) {
                    SwalCustomClass.fire({
                        text: questions[body.message],
                        icon: 'error',
                        iconColor: '#1d1d1d',
                    });
                }
            } else {
                asyncPatchQuestion(idQuestion, formModalQuestions)
                    .then((body: any) => {
                        if (body?.items !== undefined) {
                            SwalCustomClass.fire({
                                text: questions.messageEdit,
                                icon: 'success',
                                iconColor: '#1d1d1d',
                            });
                        } else if (body?.errors !== undefined) {
                            SwalCustomClass.fire({
                                text: questions[body.message],
                                icon: 'error',
                                iconColor: '#1d1d1d',
                            });
                        }
                    })
                    .finally(() => {
                        onClose(false);
                        saveLoad(true);
                        formik.handleReset;
                    });
            }
        },
    });

    useEffect(() => {
        if (idQuestion && question) {
            formik.values.type = question?.type;
            formik.values.description = question?.description;
            formik.values.icon = question?.icon || 'circle';
            formik.values.why = question?.why;
            formik.values.dependency = question?.dependency;
            formik.values.status = question?.status;

            const dep = question?.questionDependency[0];

            formik.values.questionDependency = dep?.questionDependency?.id;
            setQuestionDependency(
                dependencies.filter(
                    (d) => d.id === dep?.questionDependency?.id
                )[0] || undefined
            );

            if (dep?.questionDependency.type !== 'linear') {
                formik.values.answerDependency = dep?.answerDependency?.id;
            } else {
                formik.values.range1 = dep?.answerTextDependency?.split('-')[0];
                formik.values.range2 = dep?.answerTextDependency?.split('-')[1];
            }
        }
    }, [idQuestion, question, dependencies]);

    useEffect(() => {
        if (
            idQuestion &&
            question &&
            formik.values.dependency &&
            formik.values.questionDependency !== questionDependency?.id
        ) {
            setQuestionDependency(
                dependencies.filter(
                    (d) => d.id === formik.values.questionDependency
                )[0] || undefined
            );

            formik.values.answerDependency = '';
            formik.values.range1 = '';
        }
    }, [formik.values]);

    useEffect(() => {
        if (questionsDependency && question) {
            const data =
                questionsDependency.filter(
                    (q) =>
                        q.position < question.position &&
                        typesSupported[q?.type] &&
                        q.status !== 'inactive'
                ) || [];
            setDependencies(data);
        } else setDependencies([]);
    }, [questionsDependency, question]);

    const typeQuestions = [
        {
            id: 'multiple',
            name: 'Múltiple',
        },
        {
            id: 'only',
            name: 'Unica',
        },
        {
            id: 'linear',
            name: 'Lineal',
        },
        {
            id: 'linear-label',
            name: 'Etiquetas Lineal',
        },
        {
            id: 'text',
            name: 'Texto',
        },
        {
            id: 'big-text',
            name: 'Texto Grande',
        },
    ];

    return (
        <Modal
            open={open}
            onClose={() => onClose(false)}
            closeOnOverlayClick={false}
            styles={{ modal: { maxWidth: '500px', minWidth: '40%' } }}
        >
            <Box>
                <Typography
                    color="primary"
                    component="h3"
                    sx={{
                        fontSize: { lg: 27, xs: 23 },
                        mt: { xs: 1.5, sm: 1.5, md: 1.5, lg: 0 },
                    }}
                >
                    {idQuestion ? questions.titleEdit : questions.titleNew}
                </Typography>
                <hr />
                <Box mt={3} px={1}>
                    <Grid
                        container
                        spacing={{ xs: 1, md: 1 }}
                        columnSpacing={{ xs: 1.5, sm: 1.5, md: 1.5 }}
                    >
                        {loading ? (
                            <Loading />
                        ) : (
                            <>
                                <Grid item xs={12} mt={1}>
                                    <FieldSelect
                                        data={typeQuestions}
                                        label="Tipo Pregunta"
                                        name="type"
                                        size="small"
                                        required={true}
                                        disabled={Boolean(idQuestion)}
                                        touched={formik.touched.type}
                                        errors={formik.errors.type}
                                        value={formik.values.type}
                                        onChange={formik.handleChange}
                                    />
                                </Grid>
                                <Grid item xs={12} mt={1}>
                                    <FieldText
                                        label="Descripción"
                                        name="description"
                                        size="small"
                                        required={true}
                                        rows={3}
                                        touched={formik.touched.description}
                                        errors={formik.errors.description}
                                        value={formik.values.description}
                                        onChange={formik.handleChange}
                                    />
                                </Grid>
                                {(formik.values.type === 'linear' ||
                                    formik.values.type === 'linear-label') && (
                                    <>
                                        <Grid item xs={10} mt={1}>
                                            <FieldSelect
                                                data={typesIcon}
                                                label="Icono"
                                                name="icon"
                                                size="small"
                                                required={true}
                                                touched={formik.touched.icon}
                                                errors={formik.errors.icon}
                                                value={formik.values.icon}
                                                onChange={formik.handleChange}
                                            />
                                        </Grid>
                                        <Grid
                                            item
                                            xs={2}
                                            mt={1}
                                            display="flex"
                                            justifyContent="center"
                                            alignItems="center"
                                        >
                                            {
                                                (
                                                    typesIcon.find(
                                                        (i) =>
                                                            i.id ===
                                                            formik.values.icon
                                                    ) || typesIcon[0]
                                                )?.icon
                                            }
                                            {
                                                (
                                                    typesIcon.find(
                                                        (i) =>
                                                            i.id ===
                                                            formik.values.icon
                                                    ) || typesIcon[0]
                                                )?.icon2
                                            }
                                        </Grid>
                                    </>
                                )}
                                {typesSupported[formik?.values?.type] && (
                                    <Grid item xs={12} md={5.5} mx={0.5}>
                                        <Grid container direction="row">
                                            <Grid item xs={6} md={4} mt={0.8}>
                                                <FormLabel component="legend">
                                                    Porque:{' '}
                                                </FormLabel>
                                            </Grid>
                                            <Grid item xs={6} md={8}>
                                                <Stack
                                                    direction="row"
                                                    alignItems="center"
                                                >
                                                    <Typography>No</Typography>
                                                    <Switch
                                                        name="why"
                                                        checked={
                                                            formik.values.why
                                                        }
                                                        value={
                                                            formik.values.why
                                                        }
                                                        onChange={
                                                            formik.handleChange
                                                        }
                                                    />
                                                    <Typography>Si</Typography>
                                                </Stack>
                                            </Grid>
                                        </Grid>
                                    </Grid>
                                )}
                                {dependencies.length > 0 && (
                                    <Grid item xs={12} md={5.5} mx={0.5}>
                                        <Grid container direction="row">
                                            <Grid item xs={6} md={6} mt={0.8}>
                                                <FormLabel component="legend">
                                                    Dependencia
                                                </FormLabel>
                                            </Grid>
                                            <Grid item xs={6} md={6}>
                                                <Stack
                                                    direction="row"
                                                    alignItems="center"
                                                >
                                                    <Typography>No</Typography>
                                                    <Switch
                                                        name="dependency"
                                                        checked={
                                                            formik.values
                                                                .dependency
                                                        }
                                                        value={
                                                            formik.values
                                                                .dependency
                                                        }
                                                        onChange={() =>
                                                            formik.setFieldValue(
                                                                'dependency',
                                                                !formik.values
                                                                    .dependency
                                                            )
                                                        }
                                                    />
                                                    <Typography>Si</Typography>
                                                </Stack>
                                            </Grid>
                                        </Grid>
                                    </Grid>
                                )}
                                {formik.values.dependency &&
                                    dependencies.length > 0 && (
                                    <DependencyForm
                                        formik={formik}
                                        dependencies={dependencies}
                                        questionDependency={
                                            questionDependency
                                        }
                                    />
                                )}
                            </>
                        )}
                        <Grid item xs={12} mt={1} sx={{ textAlign: 'right' }}>
                            <ButtonGeneral
                                onClick={formik.handleSubmit}
                                startIcon={<Save color="secondary" />}
                                title="Guardar"
                                sx={{ mr: 1 }}
                            />
                            <ButtonGeneral
                                onClick={() => onClose(false)}
                                startIcon={<CancelIcon color="secondary" />}
                                title="Cancelar"
                            />
                        </Grid>
                    </Grid>
                </Box>
            </Box>
        </Modal>
    );
};

export default ModalQuestion;
